import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type Project = {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
};

export type ProjectInsert = Omit<Project, "id" | "created_at" | "updated_at">;
export type ProjectUpdate = Partial<ProjectInsert>;

// Lista de tablas que tienen relación con projects
const RELATED_TABLES = [
  "clientes",
  "forms",
  "categories",
  "subcategories",
  "questions",
  "question_values",
  "form_launches",
  "form_codes",
  "form_submissions",
  "form_answers",
  "report_templates",
  "template_prompts",
  "knowledge_base",
  "report_template_transformations",
  "launch_templates",
  "ai_generated_responses",
  "category_conclussions",
  "subcategory_conclussions",
  "template_variables_v2",
  "jotform_forms",
  "jotform_question_mappings",
];

export const useProjects = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Obtener todos los proyectos
  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Project[];
    },
  });

  // Verificar si un proyecto puede ser eliminado
  const checkProjectCanBeDeleted = async (projectId: number): Promise<{
    canDelete: boolean;
    relatedTables: string[];
    totalReferences: number;
  }> => {
    try {
      // Intentar usar la función de base de datos primero (más eficiente)
      const { data: dbCheckData, error: dbCheckError } = await supabase
        .rpc("check_project_references" as any, { project_id_param: projectId });

      if (!dbCheckError && dbCheckData && Array.isArray(dbCheckData)) {
        const relatedTables: string[] = [];
        let totalReferences = 0;

        dbCheckData.forEach((row: { table_name: string; reference_count: number }) => {
          if (row.reference_count > 0) {
            relatedTables.push(row.table_name);
            totalReferences += row.reference_count;
          }
        });

        return {
          canDelete: relatedTables.length === 0,
          relatedTables,
          totalReferences,
        };
      }
    } catch (error) {
      console.warn("Database function not available, using fallback method:", error);
    }

    // Fallback: verificar tabla por tabla
    const relatedTables: string[] = [];
    let totalReferences = 0;

    for (const table of RELATED_TABLES) {
      try {
        const { count, error } = await (supabase as any)
          .from(table)
          .select("*", { count: "exact", head: true })
          .eq("project_id", projectId);

        if (error) {
          console.error(`Error checking ${table}:`, error);
          continue;
        }

        if (count && count > 0) {
          relatedTables.push(table);
          totalReferences += count;
        }
      } catch (error) {
        console.error(`Error checking ${table}:`, error);
      }
    }

    return {
      canDelete: relatedTables.length === 0,
      relatedTables,
      totalReferences,
    };
  };

  // Crear proyecto
  const createMutation = useMutation({
    mutationFn: async (newProject: ProjectInsert) => {
      const { data, error } = await supabase
        .from("projects")
        .insert([newProject])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Proyecto creado",
        description: "El proyecto se ha creado correctamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error al crear proyecto",
        description: error.message || "Ha ocurrido un error.",
        variant: "destructive",
      });
    },
  });

  // Actualizar proyecto
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: ProjectUpdate;
    }) => {
      const { data, error } = await supabase
        .from("projects")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Proyecto actualizado",
        description: "El proyecto se ha actualizado correctamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error al actualizar proyecto",
        description: error.message || "Ha ocurrido un error.",
        variant: "destructive",
      });
    },
  });

  // Eliminar proyecto
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      // Primero verificar si puede ser eliminado
      const checkResult = await checkProjectCanBeDeleted(id);

      if (!checkResult.canDelete) {
        throw new Error(
          `No se puede eliminar el proyecto porque está siendo usado en ${checkResult.totalReferences} registro(s) en las siguientes tablas: ${checkResult.relatedTables.join(", ")}`
        );
      }

      const { error } = await supabase.from("projects").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Proyecto eliminado",
        description: "El proyecto se ha eliminado correctamente.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error al eliminar proyecto",
        description: error.message || "Ha ocurrido un error.",
        variant: "destructive",
      });
    },
  });

  return {
    projects,
    isLoading,
    createProject: createMutation.mutateAsync,
    updateProject: updateMutation.mutateAsync,
    deleteProject: deleteMutation.mutateAsync,
    checkProjectCanBeDeleted,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
